/**
 * A short, gentle confirmation beep for "document successfully processed".
 *
 * Played via WebAudio (no asset needed). It opens with a ~200ms near-silent
 * lead and uses smooth attack/release ramps so the tone eases in and out
 * instead of starting on a hard edge — that hard onset is what was clipping/
 * clicking at the start. One shared AudioContext is reused across the app.
 */

/** Silent lead before the tone, so the very start is never clipped (seconds). */
const LEAD = 0.2;
/** Tone length after the lead (seconds). */
const TONE = 0.22;
/** Tone pitch (Hz) — a soft, friendly mid-high beep. */
const FREQ = 880;

let ctx: AudioContext | undefined;

/** Lazily create (and resume) the shared AudioContext. */
function audioContext(): AudioContext | undefined {
  try {
    ctx ??= new AudioContext();
    // Autoplay policies can leave it suspended until the first user gesture.
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return undefined;
  }
}

/**
 * Play the confirmation beep. Best-effort: silent (never throws) if WebAudio is
 * unavailable or blocked before the first user gesture.
 */
export function playConfirmationBeep(): void {
  const ac = audioContext();
  if (!ac) return;
  try {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine';
    osc.frequency.value = FREQ;
    osc.connect(gain);
    gain.connect(ac.destination);

    const t0 = ac.currentTime;
    const start = t0 + LEAD; // near-silent lead
    const end = start + TONE;
    gain.gain.setValueAtTime(0.0001, t0); // hold silent through the lead
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.3, start + 0.03); // soft attack
    gain.gain.setValueAtTime(0.3, end - 0.06);
    gain.gain.exponentialRampToValueAtTime(0.0001, end); // soft release

    osc.start(t0);
    osc.stop(end + 0.02);
  } catch {
    // audio is best-effort — never let it break the flow
  }
}
